import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Columns3, Info, SearchX } from "lucide-react";
import {
  Badge,
  ChipList,
  FeedbackState,
  localTablePage,
  nextSort,
  type DataTableColumn,
  type TableSort,
} from "@h2j-data-team/ui";
import type { UnloadedColumnsQvd } from "@repo/shared";

import { formatBytes } from "@/lib/health-format";
import { qvdUnloadedColumnsInfiniteQueryOptions } from "@/features/qvd-analysis/api";
import { VirtualRowList } from "@/components/shared/VirtualRowList";
import {
  FileFormatBadge,
  ListError,
  ListLoading,
  QvdListToolbar,
  QvdPathLabel,
  RowButton,
  SpaceLink,
} from "./qvd-list-parts";
import { QvdConsumingAppsDrawer } from "./QvdConsumingAppsDrawer";
import { ALL_ROWS } from "./qvd-list-utils";

const DEFAULT_SORT: TableSort = { id: "notLoaded", direction: "desc" };

export function UnloadedColumnsTable({
  runId,
  qvdCount,
  sizeUnavailableCount = 0,
  baseUrl,
}: {
  runId: string;
  qvdCount?: number;
  // Rows whose format cannot expose per-column bytes (Parquet). Server-side
  // total (coverage), NOT a count over the pages fetched so far — otherwise the
  // disclosure under the summary changes as the user scrolls.
  sizeUnavailableCount?: number;
  baseUrl?: string | null;
}) {
  const { t } = useTranslation("qvd-analysis");
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(qvdUnloadedColumnsInfiniteQueryOptions(runId));

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const [search, setSearch] = useState("");
  const [spaceFilter, setSpaceFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<TableSort>(DEFAULT_SORT);
  // The row is kept after closing so the drawer keeps its content while it
  // animates out; the button that opened it gets the focus back.
  const [detailRow, setDetailRow] = useState<UnloadedColumnsQvd | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);

  const allRows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);

  const spaceOptions = useMemo(
    () =>
      Array.from(new Set(allRows.map((r) => r.spaceName).filter((s): s is string => !!s))).sort(),
    [allRows],
  );

  const columns = useMemo<DataTableColumn<UnloadedColumnsQvd>[]>(
    () => [
      {
        id: "name",
        header: t("columns.cols.qvd"),
        sort: "text",
        value: (r) => r.qvdName,
        rowLink: true,
        wrap: true,
        cell: (r) => {
          const notAnalyzable = r.status === "not_analyzable";
          const sized = r.sizeStatus === "sized";
          const sizeByCol = new Map(r.columnResults.map((c) => [c.column, c.sizeBytes ?? null]));
          // "direct" rows carry no badge; the other sources are mutually exclusive.
          const provenance =
            r.analysisSource === "inferred"
              ? {
                  label: t("columns.provenance.inferred"),
                  hint: t("columns.provenance.inferredHint"),
                }
              : r.analysisSource === "lineage"
                ? {
                    label: t("columns.provenance.lineage"),
                    hint: t("columns.provenance.lineageHint"),
                  }
                : r.analysisSource === "family"
                  ? {
                      label: t("columns.provenance.family"),
                      hint: t("columns.provenance.familyHint"),
                    }
                  : null;
          return (
            <div className="flex min-w-0 flex-col gap-1">
              <div className="flex min-w-0 items-center gap-2">
                <div className="min-w-0 flex-1">
                  <RowButton
                    label={t("columns.consumers.openLabel", { name: r.qvdName })}
                    onClick={(button) => {
                      opener.current = button;
                      setDetailRow(r);
                      setDetailOpen(true);
                    }}
                  >
                    <QvdPathLabel path={r.qvdPath} muted={notAnalyzable} />
                  </RowButton>
                </div>
                <FileFormatBadge path={r.qvdPath} />
                {provenance && (
                  <Badge variant="secondary" className="shrink-0 py-0" title={provenance.hint}>
                    {provenance.label}
                  </Badge>
                )}
                {notAnalyzable && (
                  <Badge variant="info" className="shrink-0 py-0">
                    <Info aria-hidden />
                    {t("columns.notAnalyzable.chip")}
                  </Badge>
                )}
              </div>
              {notAnalyzable ? (
                <p className="text-faint">{t("columns.notAnalyzable.note")}</p>
              ) : (
                <ChipList
                  className="[&>li>*]:font-mono"
                  aria-label={t("columns.notLoadedList", { name: r.qvdName })}
                  items={r.notLoadedColumns.map((col) => {
                    const bytes = sizeByCol.get(col);
                    return {
                      id: col,
                      label: sized && bytes != null ? `${col} · ${formatBytes(bytes)}` : col,
                    };
                  })}
                />
              )}
            </div>
          );
        },
      },
      {
        id: "space",
        header: t("columns.cols.space"),
        width: 170,
        value: (r) => r.spaceName,
        cell: (r) => (
          <span className="text-dim">
            <SpaceLink spaceName={r.spaceName} spaceId={r.spaceId} baseUrl={baseUrl} />
          </span>
        ),
      },
      {
        id: "notLoaded",
        header: t("columns.cols.notLoaded"),
        // Header widths fit the pt-BR labels with the sort icon.
        width: 160,
        align: "end",
        variant: "numeric",
        sort: "number",
        value: (r) => r.notLoadedCount,
        cell: (r) =>
          r.status === "not_analyzable" ? (
            <span className="text-faint">—</span>
          ) : (
            <>
              <span className="font-bold text-status-warning">{r.notLoadedCount}</span>
              <span className="text-faint">/{r.totalColumns}</span>
            </>
          ),
      },
      {
        id: "unusedBytes",
        header: t("columns.cols.unusedSize"),
        width: 160,
        align: "end",
        variant: "numeric",
        sort: "number",
        value: (r) => r.unusedBytes,
        cell: (r) => {
          if (r.status === "not_analyzable") return <span className="text-faint">—</span>;
          if (r.sizeStatus === "sized" && r.unusedBytes != null) {
            return <span className="text-status-warning">{formatBytes(r.unusedBytes)}</span>;
          }
          // The format exposes no per-column bytes at all (Parquet). NOT a
          // failure and NOT zero — it must read as "unavailable", or the row
          // claims 0 B of waste on a file nobody measured.
          if (r.sizeStatus === "size_unavailable") {
            return (
              <span className="font-sans text-faint" title={t("columns.sizeUnavailableHint")}>
                {t("columns.sizeUnavailable")}
              </span>
            );
          }
          return <span className="text-faint">—</span>;
        },
      },
    ],
    [t, baseUrl],
  );

  const rows = useMemo(
    () =>
      localTablePage(
        allRows,
        {
          page: 1,
          pageSize: ALL_ROWS,
          query: search,
          filters: { spaces: spaceFilter },
          sort,
          selection: [],
        },
        {
          columns,
          search: (r) => `${r.qvdPath} ${r.spaceName ?? ""}`,
          filter: (r, f) =>
            f.spaces.length === 0 || (r.spaceName != null && f.spaces.includes(r.spaceName)),
        },
      ).rows,
    [allRows, columns, search, spaceFilter, sort],
  );

  if (isLoading) return <ListLoading />;
  if (isError) {
    return <ListError error={error} title={t("columns.error")} onRetry={() => void refetch()} />;
  }
  if (allRows.length === 0) {
    return (
      <FeedbackState
        state="empty"
        icon={<Columns3 />}
        title={t("columns.empty.title")}
        description={t("columns.empty.description")}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <QvdListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t("columns.searchPlaceholder")}
        spaceOptions={spaceOptions}
        spaceFilter={spaceFilter}
        onSpaceFilter={setSpaceFilter}
      />

      <p className="pb-2 text-sm text-dim">
        {t("columns.summary", { count: qvdCount ?? allRows.length })}
      </p>

      {sizeUnavailableCount > 0 && (
        <p className="pb-2 text-xs text-faint">
          {t("columns.sizeUnavailableNote", { count: sizeUnavailableCount })}
        </p>
      )}

      {rows.length === 0 ? (
        <FeedbackState
          state="no-results"
          icon={<SearchX />}
          title={t("files.empty.filteredTitle")}
          description={t("files.empty.filteredDescription")}
        />
      ) : (
        <VirtualRowList
          label={t("tabs.columns")}
          columns={columns}
          rows={rows}
          getKey={(r) => r.id}
          linked={() => true}
          estimateSize={72}
          sort={sort}
          onSort={(column) => setSort(nextSort(sort, column.id, column.sort) ?? DEFAULT_SORT)}
        />
      )}

      <QvdConsumingAppsDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        runId={runId}
        row={detailRow}
        restoreFocusRef={opener}
      />
    </div>
  );
}
